import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
} from '@mui/material';
import {
  AddCircleOutline,
  Edit,
  Description,
  History,
  CloudUpload,
  Dashboard as DashboardIcon,
  ArrowBack,
  ArrowForward,
  People,
  Engineering,
  UploadFile,
  CloudUpload as CloudUploadIcon,
  CheckCircle,
  Error,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import axios from 'axios';
import QuotationGenerator from './quotation';
import QuotationEdit from './QuotationEdit';
import QuotationHistory from './QuotationHistory';
import TemplateSelector from './QuotationTemp';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const QuotationDashboard = () => {
  const [activeView, setActiveView] = useState('dashboard');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadType, setUploadType] = useState('client'); // 'client' or 'service'
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [loadingData, setLoadingData] = useState({ clients: false, services: false });

  // Function to generate and download Client Template
  const downloadClientTemplate = () => {
    const clientData = [
      ['Name', 'Address', 'Contact', 'Email', 'GST']
    ];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(clientData);
    
    const colWidths = [
      { wch: 20 }, // Name
      { wch: 30 }, // Address
      { wch: 15 }, // Contact
      { wch: 25 }, // Email
      { wch: 20 }, // GST
    ];
    ws['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, 'Client Template');
    XLSX.writeFile(wb, 'client_template.xlsx');
  };

  // Function to generate and download Services Template
  const downloadServicesTemplate = () => {
    const servicesData = [
      ['Description', 'Unit', 'Unit Price']
    ];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(servicesData);
    
    const colWidths = [
      { wch: 30 },  // Description
      { wch: 10 },  // Unit
      { wch: 15 },  // Unit Price
    ];
    ws['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, 'Services Template');
    XLSX.writeFile(wb, 'services_template.xlsx');
  };

  // Handle file selection
  const handleFileSelect = (event, type) => {
    const file = event.target.files[0];
    if (file) {
      setUploadType(type);
      setUploadFile(file);
      setUploadResult(null);
    }
  };

  // Handle file upload
  const handleUpload = async () => {
    if (!uploadFile) {
      setAlert({
        open: true,
        message: 'Please select a file first',
        severity: 'error'
      });
      return;
    }

    setUploading(true);
    setUploadResult(null);

    const formData = new FormData();
    formData.append('file', uploadFile);
    const token = localStorage.getItem('token'); 
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
    
    try {
      const endpoint = uploadType === 'client' 
        ? `${API_URL}/api/quotation-import/upload-client` 
        : `${API_URL}/api/quotation-import/upload-service`;

      const response = await axios.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        },
        withCredentials: true
      });

      setUploadResult(response.data);
      setUploadFile(null);
      
      // Clear file input
      document.querySelectorAll('input[type="file"]').forEach(input => input.value = '');
      
      setAlert({
        open: true,
        message: response.data.message,
        severity: 'success'
      });

    } catch (error) {
      //console.error('Upload error:', error);
      setUploadResult({
        message: 'Upload failed',
        failed: [{ error: error.response?.data?.message || error.message }]
      });
      setAlert({
        open: true,
        message: error.response?.data?.message || 'Upload failed',
        severity: 'error'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleUploadOpen = () => {
    setActiveView('upload');
  };

  const dashboardCards = [
    {
      id: 'generate',
      title: 'Generate New Quotation',
      description: 'Create a fresh quotation manually or from scratch',
      icon: <AddCircleOutline fontSize="large" />,
      color: '#1976d2',
      action: () => {
        sessionStorage.removeItem('editQuotation');
        setSelectedTemplate(null);
        setActiveView('generate');
      },
    },
    {
      id: 'open',
      title: 'Open / Edit Draft',
      description: 'Edit existing draft quotations',
      icon: <Edit fontSize="large" />,
      color: '#9c27b0',
      action: () => setActiveView('open'),
    },
    {
      id: 'template',
      title: 'Use Template',
      description: 'Start from pre-designed quotation templates',
      icon: <Description fontSize="large" />,
      color: '#2e7d32',
      action: () => setActiveView('template'),
    },
    {
      id: 'history',
      title: 'Quotation History',
      description: 'View and manage all previous quotations',
      icon: <History fontSize="large" />,
      color: '#ed6c02',
      action: () => setActiveView('history'),
    },
    {
      id: 'upload',
      title: 'Upload Client & Services',
      description: 'Import client info and service catalog via Excel',
      icon: <CloudUpload fontSize="large" />,
      color: '#0288d1',
      action: handleUploadOpen,
    },
  ];

  const fixedCardHeight = 270;

  const renderActiveView = () => {
    switch (activeView) {
      case 'generate':
        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <IconButton
                onClick={() => setActiveView('dashboard')}
                sx={{ mr: 2 }}
                aria-label="Back to dashboard"
                size="large"
              >
                <ArrowBack fontSize="inherit" />
              </IconButton>
              <Typography variant="h4" fontWeight="bold" color="text.primary">
                Generate New Quotation
              </Typography>
            </Box>
            <QuotationGenerator
              onBack={() => setActiveView('dashboard')}
              initialData={selectedTemplate}
            />
          </Box>
        );
      case 'open':
        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <IconButton
                onClick={() => setActiveView('dashboard')}
                sx={{ mr: 2 }}
                aria-label="Back to dashboard"
                size="large"
              >
                <ArrowBack fontSize="inherit" />
              </IconButton>
              <Typography variant="h4" fontWeight="bold" color="text.primary">
                Open / Edit Draft Quotations
              </Typography>
            </Box>
            <QuotationEdit
              onBack={() => setActiveView('dashboard')}
              onEdit={() => setActiveView('generate')}
            />
          </Box>
        );
      case 'template':
        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <IconButton
                onClick={() => setActiveView('dashboard')}
                sx={{ mr: 2 }}
                aria-label="Back to dashboard"
                size="large"
              >
                <ArrowBack fontSize="inherit" />
              </IconButton>
              <Typography variant="h4" fontWeight="bold" color="text.primary">
                Select Template
              </Typography>
            </Box>
            <TemplateSelector
              onSelectTemplate={(template) => {
                setSelectedTemplate(template);
                setActiveView('generate');
              }}
              onBack={() => setActiveView('dashboard')}
            />
          </Box>
        );
      case 'history':
        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <IconButton
                onClick={() => setActiveView('dashboard')}
                sx={{ mr: 2 }}
                aria-label="Back to dashboard"
                size="large"
              >
                <ArrowBack fontSize="inherit" />
              </IconButton>
              <Typography variant="h4" fontWeight="bold" color="text.primary">
                Quotation History
              </Typography>
            </Box>
            <QuotationHistory onBack={() => setActiveView('dashboard')} />
          </Box>
        );
      case 'upload':
        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <IconButton
                onClick={() => setActiveView('dashboard')}
                sx={{ mr: 2 }}
                aria-label="Back to dashboard"
                size="large"
              >
                <ArrowBack fontSize="inherit" />
              </IconButton>
              <Typography variant="h4" fontWeight="bold" color="text.primary">
                Upload Client & Services
              </Typography>
            </Box>
            
            {/* Upload Section UI */}
            <Grid container spacing={3}>
              {/* Client Upload Section */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <People color="primary" sx={{ mr: 2 }} />
                      <Typography variant="h6" fontWeight="medium">
                        Upload Client Information
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Import client details from CSV or Excel format:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mb: 3 }}>
                      <li><Typography variant="body2"><strong>Name</strong> - Client name</Typography></li>
                      <li><Typography variant="body2"><strong>Address</strong> - Complete address</Typography></li>
                      <li><Typography variant="body2"><strong>Contact</strong> - Contact number</Typography></li>
                      <li><Typography variant="body2"><strong>Email</strong> - Email address</Typography></li>
                      <li><Typography variant="body2"><strong>GST</strong> - GST number</Typography></li>
                    </Box>
                    
                    {/* File upload section */}
                    <Box sx={{ mb: 2 }}>
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<UploadFile />}
                        fullWidth
                      >
                        Select Client Excel File
                        <input 
                          type="file" 
                          hidden 
                          accept=".csv,.xlsx,.xls" 
                          onChange={(e) => handleFileSelect(e, 'client')}
                        />
                      </Button>
                      {uploadFile && uploadType === 'client' && (
                        <Typography variant="body2" sx={{ mt: 1, color: 'success.main' }}>
                          Selected: {uploadFile.name}
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        onClick={handleUpload}
                        disabled={uploading || !uploadFile || uploadType !== 'client'}
                        startIcon={uploading && uploadType === 'client' ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                      >
                        {uploading && uploadType === 'client' ? 'Uploading...' : 'Upload Clients'}
                      </Button>
                      <Button 
                        variant="outlined" 
                        onClick={downloadClientTemplate}
                        startIcon={<Description />}
                      >
                        Download Template
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Services Upload Section */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Engineering color="secondary" sx={{ mr: 2 }} />
                      <Typography variant="h6" fontWeight="medium">
                        Upload Service Catalog
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Import your service catalog:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mb: 3 }}>
                      <li><Typography variant="body2"><strong>Description</strong> - Service description</Typography></li>
                      <li><Typography variant="body2"><strong>Unit</strong> - Measurement unit</Typography></li>
                      <li><Typography variant="body2"><strong>Unit Price</strong> - Price per unit</Typography></li>
                    </Box>
                    
                    {/* File upload section */}
                    <Box sx={{ mb: 2 }}>
                      <Button
                        variant="outlined"
                        color="secondary"
                        component="label"
                        startIcon={<UploadFile />}
                        fullWidth
                      >
                        Select Services Excel File
                        <input 
                          type="file" 
                          hidden 
                          accept=".csv,.xlsx,.xls" 
                          onChange={(e) => handleFileSelect(e, 'service')}
                        />
                      </Button>
                      {uploadFile && uploadType === 'service' && (
                        <Typography variant="body2" sx={{ mt: 1, color: 'success.main' }}>
                          Selected: {uploadFile.name}
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleUpload}
                        disabled={uploading || !uploadFile || uploadType !== 'service'}
                        startIcon={uploading && uploadType === 'service' ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                      >
                        {uploading && uploadType === 'service' ? 'Uploading...' : 'Upload Services'}
                      </Button>
                      <Button 
                        variant="outlined" 
                        color="secondary"
                        onClick={downloadServicesTemplate}
                        startIcon={<Description />}
                      >
                        Download Template
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Upload Result Display */}
              {uploadResult && (
                <Grid item xs={12}>
                  <Card 
                    variant="outlined" 
                    sx={{ 
                      borderColor: uploadResult.failed ? 'error.main' : 'success.main',
                      bgcolor: uploadResult.failed ? 'error.light' : 'success.light'
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        {uploadResult.failed ? (
                          <Error color="error" sx={{ mr: 1 }} />
                        ) : (
                          <CheckCircle color="success" sx={{ mr: 1 }} />
                        )}
                        <Typography variant="subtitle1" fontWeight="bold">
                          {uploadResult.message}
                        </Typography>
                      </Box>
                      
                      <Typography variant="body2">
                        {uploadResult.new_clients && `New Clients: ${uploadResult.new_clients}`}
                        {uploadResult.updated_clients && ` | Updated Clients: ${uploadResult.updated_clients}`}
                        {uploadResult.new_services && `New Services: ${uploadResult.new_services}`}
                        {uploadResult.updated_services && ` | Updated Services: ${uploadResult.updated_services}`}
                        {uploadResult.total_processed && ` | Total Processed: ${uploadResult.total_processed}`}
                      </Typography>

                      {uploadResult.failed && uploadResult.failed.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" fontWeight="bold" color="error">
                            Failed Rows ({uploadResult.failed.length}):
                          </Typography>
                          <Box component="pre" sx={{ 
                            mt: 1, 
                            p: 1, 
                            bgcolor: 'grey.100', 
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            maxHeight: '200px',
                            overflow: 'auto'
                          }}>
                            {uploadResult.failed.map((fail, index) => (
                              <div key={index}>
                                Row: {JSON.stringify(fail.row)} - Error: {fail.error}
                              </div>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button 
                variant="outlined" 
                onClick={() => {
                  setActiveView('dashboard');
                  setUploadFile(null);
                  setUploadResult(null);
                }}
              >
                Back to Dashboard
              </Button>
            </Box>
          </Box>
        );
      default:
        return renderDashboard();
    }
  };

  const renderDashboard = () => (
    <Box>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 4,
          borderRadius: 3,
          boxShadow: 'none',
          border: '1px solid #e0e0e0',
          bgcolor: '#ffffff'
        }}
      >
        <Typography
          variant="h4"
          gutterBottom
          fontWeight="bold"
          sx={{ display: 'flex', alignItems: 'center', gap: 2, color: '#333' }}
        >
          Quotation Management
        </Typography>
        <Typography
          sx={{ opacity: 0.85, maxWidth: '800px', color: '#555' }}
        >
          Create, manage, and send professional quotations for industrial electronic board repair and retrofitting.
        </Typography>
      </Paper>

      <Typography
        variant="h5"
        gutterBottom
        fontWeight="bold"
        sx={{ mb: 3, color: '#0d47a1' }}
      >
        Quick Actions
      </Typography>

      <Grid container spacing={4} sx={{ justifyContent: 'center' }}>
        {dashboardCards.map((card, index) => {
          const gridSize = index < 3 ? { xs: 12, sm: 6, md: 4 } : { xs: 12, sm: 6, md: 6 };
          return (
            <Grid
              item
              key={card.id}
              {...gridSize}
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              <Card
                sx={{
                  width: '100%',
                  maxWidth: 360,
                  height: fixedCardHeight,
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
                  },
                  borderLeft: `6px solid ${card.color}`,
                  bgcolor: '#ffffff',
                  borderRadius: 2,
                  boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                }}
                elevation={0}
              >
                <CardContent sx={{ flexGrow: 1, px: 4, py: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, color: card.color }}>
                    {card.icon}
                    <Typography variant="h6" component="h2" sx={{ ml: 2, fontWeight: 'bold', color: '#333' }}>
                      {card.title}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.95rem', lineHeight: 1.6, color: '#666' }}>
                    {card.description}
                  </Typography>
                </CardContent>
                <CardActions sx={{ px: 4, pb: 3 }}>
                  <Button
                    size="medium"
                    onClick={card.action}
                    fullWidth
                    variant="contained"
                    sx={{
                      bgcolor: card.color,
                      color: '#fff',
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 1,
                      boxShadow: 'none',
                      '&:hover': {
                        bgcolor: card.color,
                        opacity: 0.9,
                        boxShadow: 'none',
                      },
                    }}
                  >
                    Select
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3,  minHeight: '100vh' }}>
      {renderActiveView()}
    </Container>
  );
};

export default QuotationDashboard;